import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Menu, message } from "antd";
import "./sidebar.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SidebarData } from "./SidebarData";
import { ensureSidebarItems } from "./ensureSidebarItems";
import { FlexIconLogo, LogoKryesore } from "../../assets/icons";
import {
  FlexHalloweenLogo,
  ChristmasFlexLogo,
  EagleAndLogo,
} from "../../assets/holidays/icons";
import { useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import useOrariAccess from "../Orari/hooks/useOrariAccess";
import { useAuth } from "../../authentication/authHelpers";
import { promptPagatPin } from "../../utils/promptPagatPin";
import { isPagatPinUnlocked } from "../../utils/getCurrentUserPin";
import { useSeasonalTheme } from "../../utils/seasonalTheme";

function Sidebar({ data, collapsed }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();

  const { users, isDarkMode } = useSelector((state) => state.usersList);
  const { allowed: canAccessOrari } = useOrariAccess();
  const { active: seasonalTheme } = useSeasonalTheme();

  const [k, setK] = useState([]);
  const [sData, setSData] = useState([]);
  const [pagatFocus, setPagatFocus] = useState(null);

  const openPagatWithPin = useCallback(async () => {
    if (isPagatPinUnlocked()) {
      navigate("/pagat");
      return;
    }
    const ok = await promptPagatPin({
      users,
      authUser: auth?.user || auth?.userAccess?.[0],
      isDarkMode,
    });
    if (ok) navigate("/pagat");
  }, [users, auth, isDarkMode, navigate]);

  useEffect(() => {
    let pathname = loc.pathname;
    if (pathname.includes("specifikat"))
      pathname = pathname.split("/").slice(0, -1).join("/");

    // Menu item keys use route `to` (unique). Prefer exact path, then prefix.
    let match = SidebarData.find((el) => el.to === pathname);
    if (!match) {
      match = SidebarData.find(
        (el) => el.to !== "/" && pathname.startsWith(`${el.to}/`)
      );
    }
    if (!match) {
      match = SidebarData.find((el) => el.to.includes(pathname));
    }
    if (match) setK([match.to]);

    if (loc?.pathname === "/pagat" || loc?.pathname === "/pagat/llogaritja") {
      setPagatFocus("pagat");
    } else {
      setPagatFocus(null);
    }
  }, [loc]);

  useEffect(() => {
    if (data.length > 0) {
      setSData(data);
    }
  }, [data]);

  const isMobileView = useMediaQuery({
    query: "(max-width: 450px)",
  });

  const menuItems = useMemo(() => {
    const raw = Array.isArray(data[0]?.accessConfig?.sidebarConfig[0])
      ? data[0]?.accessConfig?.sidebarConfig[0]
      : data[0]?.accessConfig?.sidebarConfig;

    const userRole =
      auth?.user?.userRole || auth?.userAccess?.[0]?.userRole || "";
    // Saved accessConfig often predates new menu entries — inject required ones.
    const config = ensureSidebarItems(raw || [], userRole);

    return config
      .filter((a) => a.auth !== false)
      .filter((a) => a?.to !== "/orari" || canAccessOrari)
      .filter((a) => !!SidebarData.find((s) => s.to === a?.to)?.src)
      .map((el) => {
        const Icon = SidebarData.find((s) => s.to === el.to)?.src;
        const iconNode = (
          <span className="iconWrapper">
            <Icon
              width={el?.to === "/pagat" ? 22 : 18}
              height={el?.to === "/pagat" ? 20 : 16}
            />
          </span>
        );
        const itemStyle = {
          display: "flex",
          flexDirection: `${!!collapsed ? "column" : "row"}`,
        };

        const itemKey = el?.to || String(el?.id);

        if (el?.to === "/pagat") {
          return {
            key: itemKey,
            icon: iconNode,
            className: pagatFocus === "pagat" ? "Focus-Show" : "",
            style: itemStyle,
            label: <span>{!collapsed && el?.title}</span>,
            onClick: () => {
              openPagatWithPin().catch((err) => {
                console.error(err);
                message.error("Nuk u hap prompti i kodit.");
              });
            },
          };
        }

        return {
          key: itemKey,
          icon: iconNode,
          title: el?.title,
          style: itemStyle,
          label: (
            <Link to={el?.to} rel="noopener noreferrer">
              {!collapsed && el?.title}
            </Link>
          ),
        };
      });
  }, [
    data,
    collapsed,
    pagatFocus,
    isDarkMode,
    canAccessOrari,
    openPagatWithPin,
    auth,
  ]);

  return (
    <div className={`side-bar ${isDarkMode ? "dark" : ""}`}>
      {!isMobileView ? (
        <Link to="/kryefaqja">
          <div className="sidebar-logo">
            {collapsed ? (
              <FlexIconLogo />
            ) : seasonalTheme?.effects?.logo &&
              seasonalTheme.id === "halloween" ? (
              <FlexHalloweenLogo width={220} height={56} />
            ) : seasonalTheme?.effects?.logo &&
              seasonalTheme.id === "winter" ? (
              <ChristmasFlexLogo width={220} height={56} />
            ) : seasonalTheme?.effects?.logo &&
              seasonalTheme.id === "albaniaFlag" ? (
              <EagleAndLogo width={220} height={56} />
            ) : (
              <LogoKryesore />
            )}
          </div>
        </Link>
      ) : null}
      {sData?.length > 0 && (
        <Menu
          mode="inline"
          selectedKeys={k}
          className="main-sidebar"
          items={menuItems}
        />
      )}
    </div>
  );
}

export default Sidebar;
